import type { CSSProperties, MouseEvent } from 'react';
import type { SimpleRect } from './Tour';

interface TourOverlayProps {
  rect: SimpleRect | null;
  onClick?: () => void;
  /**
   * Si el paso activo es `advanceOn`, los divs oscuros NO llaman a `onClick`: un click
   * fallido fuera del target no debe cerrar el tour por accidente (D5). El cierre queda
   * disponible solo por la "X" del popover.
   */
  isAdvanceOn?: boolean;
}

const OVERLAY_PADDING = 8;
const OVERLAY_RADIUS = 14;

/**
 * Overlay del tour: un único div del tamaño del cutout con un `box-shadow` de spread
 * enorme que oscurece toda la viewport EXCEPTO el rectángulo del div (patrón
 * react-joyride/shepherd). El `border-radius` del div redondea el hueco; al ser una sola
 * pieza no hay costuras (la versión de 4 divs dejaba una línea clara entre ellos) ni
 * esquinas rectas. Sin `rect` (paso centrado) → un único div fullscreen oscuro.
 *
 * El cutout sigue `rect` (el `animatedRect` del motor) con la misma transición que el
 * spotlight, sin regresión visual. El hueco es CLICABLE porque el div del cutout tiene
 * `pointer-events: none`; el click-fuera se captura con un backdrop fullscreen detrás.
 *
 * D5: en pasos `advanceOn` el backdrop no cierra el tour (un click fallido fuera del
 * target no debe cerrarlo por accidente); el cierre queda solo por la "X" del popover.
 *
 * advanceOn + click-through (bug fix): en pasos `advanceOn` NO se renderiza el backdrop
 * interactivo fullscreen. Ese backdrop (pointer-events:auto, dentro del portal z-index
 * 9000) tapaba el target y robaba el click → `e.target` era el backdrop, `closest` fallaba
 * y el paso no avanzaba (ni se disparaba lo que el botón real abre). El cutout
 * (pointer-events:none) sigue pintando toda la oscuridad por su box-shadow, así que el
 * dim se mantiene mientras el hueco sobre el target queda totalmente libre para el click.
 * Si el target aún no tiene rect (drawer no montado), el fullscreen va con
 * `pointer-events:none` para no bloquear el click cuando el elemento aparezca tarde.
 */
export function TourOverlay({ rect, onClick, isAdvanceOn }: TourOverlayProps) {
  // En pasos advanceOn el click en la zona oscura no cierra el tour (D5).
  const handleClick = isAdvanceOn ? undefined : onClick;

  // Sin target → un único div fullscreen que oscurece toda la viewport. En advanceOn no
  // debe capturar clicks: el target puede montarse tarde (drawer) y el click ha de llegarle.
  if (!rect) {
    return (
      <div
        className="klonup-tour-overlay klonup-tour-overlay-full"
        onClick={handleClick}
        style={{ position: 'fixed', inset: 0, ...(isAdvanceOn ? { pointerEvents: 'none' } : {}) }}
      />
    );
  }

  const top = rect.top - OVERLAY_PADDING;
  const left = rect.left - OVERLAY_PADDING;
  const width = rect.width + OVERLAY_PADDING * 2;
  const height = rect.height + OVERLAY_PADDING * 2;

  const cutoutStyle: CSSProperties = {
    position: 'fixed',
    top: `${top}px`,
    left: `${left}px`,
    width: `${Math.max(0, width)}px`,
    height: `${Math.max(0, height)}px`,
    borderRadius: `${OVERLAY_RADIUS}px`,
    // advanceOn: el hueco NO intercepta clicks → el target real recibe el evento (necesario para
    // avanzar al pulsarlo). Paso lineal: el hueco SÍ captura el click (pointer-events:auto) para
    // bloquear la interacción con la página real, pero sin cerrar el tour (el click se ignora).
    pointerEvents: isAdvanceOn ? 'none' : 'auto',
  };

  // Paso lineal: tragar el click del hueco (no propaga, no cierra). advanceOn: sin handler.
  const handleCutoutClick = isAdvanceOn
    ? undefined
    : (e: MouseEvent) => e.stopPropagation();

  return (
    <>
      {/* Backdrop fullscreen detrás: captura el click-fuera para cerrar (solo pasos lineales, D5).
          En advanceOn NO se renderiza: taparía el target y robaría el click (la oscuridad la
          sigue dando el box-shadow del cutout, así que el dim se mantiene). */}
      {!isAdvanceOn && (
        <div
          className="klonup-tour-overlay klonup-tour-overlay-backdrop"
          data-tour-overlay="backdrop"
          onClick={handleClick}
          style={{ position: 'fixed', inset: 0 }}
        />
      )}
      {/* Cutout: hueco redondeado pintado por el box-shadow gigante de .klonup-tour-cutout. */}
      <div className="klonup-tour-cutout" data-tour-overlay="cutout" onClick={handleCutoutClick} style={cutoutStyle} />
    </>
  );
}
